import { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APP_CONFIG, AUTH_PROFILES } from '@/config/app.config';
import { isTeamProfile } from '@/services/userPermissions';
import ApperIcon from '@/components/ApperIcon';
import ThemeToggle from '@/components/ThemeToggle';
import BrandLogo from '@/components/BrandLogo';
import HauntBackground from '@/components/art/HauntBackground';
import PaymentNotice from '@/components/PaymentNotice';
import WhatsAppContact from '@/components/WhatsAppContact';
import LanguagePicker from '@/components/LanguagePicker';
import { useT } from '@/shared/i18n';
import { INSTALL_PATH } from '@/shared/pwa';
import { STATUS_PATH } from '@/shared/status';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SECTIONS = [
  { hash: '#plans', label: 'Plans' },
  { hash: '#devices', label: 'Devices' },
  { hash: '#reviews', label: 'Reviews' },
  { hash: '#faq', label: 'FAQ' },
  { hash: '#contact', label: 'Contact' },
];

const PORTAL_LABELS = {
  customer: 'Customer sign in',
  admin: 'Owner sign in',
  staff: 'Staff sign in',
};

function AuthArea({ user }) {
  const { t } = useT();
  const portals = AUTH_PROFILES ?? [];
  if (user?.Id || user?.userId) {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.emailAddress;
    const dashboard = isTeamProfile(user.profile) ? '/admin' : '/account';
    return (
      <div className="flex items-center gap-2">
        <span className="hidden max-w-[10rem] truncate text-sm text-muted-foreground lg:inline">{name}</span>
        <Button size="sm" asChild>
          <Link to={dashboard}>
            <ApperIcon name="LayoutDashboard" size={16} />
            {isTeamProfile(user.profile) ? t('Console') : t('My account')}
          </Link>
        </Button>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline">
          <ApperIcon name="LogIn" size={16} />
          {t('Sign in')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">{t('Choose your portal')}</DropdownMenuLabel>
        {portals.map((p) => (
          <DropdownMenuItem key={p.key} asChild>
            <Link to={`/${p.key}/login`}>{PORTAL_LABELS[p.key] ? t(PORTAL_LABELS[p.key]) : `${p.label} sign in`}</Link>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/customer/signup">{t('Create a customer account')}</Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function PublicLayout() {
  const user = useSelector((s) => s.user.user);
  const [open, setOpen] = useState(false);
  const { t } = useT();

  return (
    <div className="relative flex min-h-svh flex-col bg-background text-foreground">
      <HauntBackground />
      <header className="sticky top-0 z-(--z-sticky) border-b border-border/60 bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-(--spacing-header) max-w-6xl items-center gap-4 px-4 md:px-8">
          <Link to="/" className="flex items-center gap-2" aria-label={`${APP_CONFIG.name} home`}>
            <BrandLogo size={30} />
            <span className="font-heading text-2xl leading-none">{APP_CONFIG.name}</span>
          </Link>
          <nav className="ml-6 hidden items-center gap-1 md:flex" aria-label="Sections">
            {SECTIONS.map((s) => (
              <Link
                key={s.hash}
                to={{ pathname: '/', hash: s.hash }}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                {t(s.label)}
              </Link>
            ))}
          </nav>
          <div className="ms-auto flex items-center gap-2">
            <LanguagePicker />
            <ThemeToggle className="hidden sm:flex" />
            <AuthArea user={user} />
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label="Toggle sections"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
            >
              <ApperIcon name={open ? 'X' : 'Menu'} size={20} />
            </Button>
          </div>
        </div>
        {open && (
          <nav className="border-t border-border/60 px-4 py-3 md:hidden" aria-label="Sections">
            <div className="grid gap-1">
              {SECTIONS.map((s) => (
                <Link
                  key={s.hash}
                  to={{ pathname: '/', hash: s.hash }}
                  onClick={() => setOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  {t(s.label)}
                </Link>
              ))}
            </div>
            <div className="mt-3 grid gap-3 border-t border-border/60 pt-3">
              <Link
                to={INSTALL_PATH}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-primary hover:bg-accent hover:text-accent-foreground"
              >
                <ApperIcon name="Smartphone" size={16} />
                {t('Install the app')}
              </Link>
              <ThemeToggle className="w-full justify-between" />
            </div>
          </nav>
        )}
      </header>

      <div className="relative z-10 flex-1">
        <Outlet />
      </div>

      <footer className="relative z-10 border-t border-border/60">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-4 py-10 md:flex-row md:items-end md:justify-between md:px-8">
          <div>
            <p className="flex items-center gap-2 font-heading text-3xl leading-none">
              <BrandLogo size={36} />
              {APP_CONFIG.name}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{APP_CONFIG.tagline}</p>
            <Link
              to={INSTALL_PATH}
              className="group mt-5 inline-flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 transition-colors hover:border-primary/60 hover:bg-primary/10"
            >
              <ApperIcon name="Smartphone" size={18} className="text-primary" />
              <span>
                <span className="block text-sm font-medium">{t('Install Ghost Cable as an app')}</span>
                <span className="block text-xs text-muted-foreground">{t('Home screen or desktop · how-to for every device')}</span>
              </span>
              <ApperIcon name="ArrowRight" size={16} className="text-primary transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
          <div className="flex flex-col gap-3 text-sm md:items-end">
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              <Link to="/terms" className="text-muted-foreground hover:text-primary">{t('Terms')}</Link>
              <Link to="/privacy" className="text-muted-foreground hover:text-primary">{t('Privacy')}</Link>
              <Link to="/refund-policy" className="text-muted-foreground hover:text-primary">{t('No-refund policy')}</Link>
              <Link to="/customer/login" className="text-muted-foreground hover:text-primary">{t('My account')}</Link>
              <Link to="/compare" className="text-muted-foreground hover:text-primary">{t('Compare plans')}</Link>
              <Link to="/check-order" className="text-muted-foreground hover:text-primary">{t('Check an order')}</Link>
              <Link to={STATUS_PATH} className="text-muted-foreground hover:text-primary">{t('Service status')}</Link>
              <Link to={INSTALL_PATH} className="text-muted-foreground hover:text-primary">{t('How to install')}</Link>
            </div>
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} Ghost Cable. {t('All programming is original.')}</p>
          </div>
        </div>
      </footer>
      <PaymentNotice />
      {!(user?.Id || user?.userId) && <WhatsAppContact />}
    </div>
  );
}
